import { Box, Divider, Stack, Text } from '@mantine/core';

import { AbstractOwnerProofList } from '@/module/Author/Abstract/components/AbstractOwnerProofList';
import { AbstractCard } from '@/module/Author/Abstract/components/Card/AbstractCard';
import { ArticleBestQ } from '@/module/Author/Article/components/ArticleBestQ';
import { ArticleImpactFactor } from '@/module/Author/Article/components/ArticleImpactFactor';
import { ArticleInfluence } from '@/module/Author/Article/components/ArticleInfluence';
import { ArticleOwnerProofList } from '@/module/Author/Article/components/ArticleOwnerProofList';
import { ArticleCard } from '@/module/Author/Article/components/Card/ArticleCard';
import { AwardOwnerProofList } from '@/module/Author/Award/components/AwardOwnerProofList';
import { AwardCard } from '@/module/Author/Award/components/Card/AwardCard';
import { BookOwnerProofList } from '@/module/Author/Book/components/BookOwnerProofList';
import { BookCard } from '@/module/Author/Book/components/Card/BookCard';
import { ChapterCard } from '@/module/Author/Chapter/components/Card/ChapterCard';
import { ChapterOwnerProofList } from '@/module/Author/Chapter/components/ChapterOwnerProofList';
import { CommitteeCard } from '@/module/Author/Committee/components/Card/CommitteeCard';
import { CommitteeOwnerProofList } from '@/module/Author/Committee/components/CommitteeOwnerProofList';
import { CourseCard } from '@/module/Author/Course/components/Card/CourseCard';
import { CourseOwnerProofList } from '@/module/Author/Course/components/CourseOwnerProofList';
import { DictionaryCard } from '@/module/Author/Dictionary/components/Card/DictionaryCard';
import { DictionaryOwnerProofList } from '@/module/Author/Dictionary/components/DictionaryOwnerProofList';
import { DoctoralCommitteeCard } from '@/module/Author/DoctoralCommittee/components/Card/DoctoralCommitteeCard';
import { DoctoralCommitteeOwnerProofList } from '@/module/Author/DoctoralCommittee/components/DoctoralCommitteeOwnerProofList';
import { EditorialTeamCard } from '@/module/Author/EditorialTeam/components/Card/EditorialTeamCard';
import { EditorialTeamOwnerProofList } from '@/module/Author/EditorialTeam/components/EditorialTeamOwnerProofList';
import { PatentCard } from '@/module/Author/Patent/components/Card/PatentCard';
import { PatentOwnerProofList } from '@/module/Author/Patent/components/PatentOwnerProofList';
import { PresentationCard } from '@/module/Author/Presentation/components/Card/PresentationCard';
import { PresentationOwnerProofList } from '@/module/Author/Presentation/components/PresentationOwnerProofList';
import { ProceedingCard } from '@/module/Author/Proceeding/components/Card/ProceedingCard';
import { ProceedingOwnerProofList } from '@/module/Author/Proceeding/components/ProceedingOwnerProofList';
import { ProjectCard } from '@/module/Author/Project/components/Card/ProjectCard';
import { TranslationCard } from '@/module/Author/Translation/components/Card/TranslationCard';
import { TranslationOwnerProofList } from '@/module/Author/Translation/components/TranslationOwnerProofList';
import {
  EvaluationItem,
  isEvalCriteriaAbstractItem,
  isEvalCriteriaArticleItem,
  isEvalCriteriaAwardItem,
  isEvalCriteriaBookItem,
  isEvalCriteriaBooleanItem,
  isEvalCriteriaChapterItem,
  isEvalCriteriaCommitteeItem,
  isEvalCriteriaCourseItem,
  isEvalCriteriaDictionaryItem,
  isEvalCriteriaDoctoralCommitteeItem,
  isEvalCriteriaEditorialTeamItem,
  isEvalCriteriaFreeItem,
  isEvalCriteriaNumberItem,
  isEvalCriteriaPatentItem,
  isEvalCriteriaPresentationItem,
  isEvalCriteriaProceedingItem,
  isEvalCriteriaProjectItem,
  isEvalCriteriaTranslationItem,
} from '@/module/ResearchPerfEval/model/EvalTemplateTypes';

import { Id, assertUnreachable } from '@feature/utils';

interface ResearchPerfEvalItemCardHeaderProps {
  item: EvaluationItem;
  dataEntryHint: string | null;

  userId: Id;
}

export function ResearchPerfEvalItemCardHeader({ item, dataEntryHint, userId }: ResearchPerfEvalItemCardHeaderProps) {
  if (isEvalCriteriaFreeItem(item)) {
    return <Text>{item.activityDescription}</Text>;
  }

  if (isEvalCriteriaBooleanItem(item)) {
    return <Text fw="500">DA</Text>;
  }

  if (isEvalCriteriaNumberItem(item)) {
    return (
      <Text>
        {dataEntryHint}: {item.number}
      </Text>
    );
  }

  if (isEvalCriteriaCourseItem(item)) {
    return (
      <Stack>
        <CourseCard
          course={item.course}
          onClick={() => window.open(`/author/course/${item.course.id}`)}
        />
        <CourseOwnerProofList
          courseId={item.course.id}
          userId={userId}
        />
      </Stack>
    );
  }

  if (isEvalCriteriaAbstractItem(item)) {
    return (
      <Stack>
        <AbstractCard
          abstract={item.abstractItem}
          onClick={() => window.open(`/author/abstract/${item.abstractItem.id}`)}
        />
        <AbstractOwnerProofList
          abstractId={item.abstractItem.id}
          userId={userId}
        />
      </Stack>
    );
  }

  if (isEvalCriteriaArticleItem(item)) {
    return (
      <Box flex={1}>
        <ArticleCard
          article={item.article}
          onClick={() => window.open(`/author/article/${item.article.id}`)}
        />
        <Box p="10px">
          <ArticleImpactFactor
            hideTemporary
            articleId={item.article.id}
          />
          <Divider my="10px" />
          <ArticleInfluence
            hideTemporary
            articleId={item.article.id}
          />
          <Divider my="10px" />
          <ArticleBestQ articleId={item.article.id} />
          <Divider my="10px" />
          <ArticleOwnerProofList
            articleId={item.article.id}
            userId={userId}
          />
        </Box>
      </Box>
    );
  }

  if (isEvalCriteriaBookItem(item)) {
    return (
      <Stack>
        <BookCard
          book={item.book}
          onClick={() => window.open(`/author/book/${item.book.id}`)}
        />
        <BookOwnerProofList
          bookId={item.book.id}
          userId={userId}
        />
      </Stack>
    );
  }

  if (isEvalCriteriaTranslationItem(item)) {
    return (
      <Stack>
        <TranslationCard
          translation={item.translation}
          onClick={() => window.open(`/author/translation/${item.translation.id}`)}
        />
        <TranslationOwnerProofList
          translationId={item.translation.id}
          userId={userId}
        />
      </Stack>
    );
  }

  if (isEvalCriteriaChapterItem(item)) {
    return (
      <Stack>
        <ChapterCard
          chapter={item.chapter}
          onClick={() => window.open(`/author/chapter/${item.chapter.id}`)}
        />
        <ChapterOwnerProofList
          chapterId={item.chapter.id}
          userId={userId}
        />
      </Stack>
    );
  }

  if (isEvalCriteriaProceedingItem(item)) {
    return (
      <Stack>
        <ProceedingCard
          proceeding={item.proceeding}
          onClick={() => window.open(`/author/proceeding/${item.proceeding.id}`)}
        />
        <ProceedingOwnerProofList
          proceedingId={item.proceeding.id}
          userId={userId}
        />
      </Stack>
    );
  }

  if (isEvalCriteriaPatentItem(item)) {
    return (
      <Stack>
        <PatentCard
          patent={item.patent}
          onClick={() => window.open(`/author/patent/${item.patent.id}`)}
        />
        <PatentOwnerProofList
          patentId={item.patent.id}
          userId={userId}
        />
      </Stack>
    );
  }

  if (isEvalCriteriaProjectItem(item)) {
    return (
      <ProjectCard
        project={item.project}
        onClick={() => window.open(`/author/project/${item.project.id}`)}
      />
    );
  }

  if (isEvalCriteriaCommitteeItem(item)) {
    return (
      <Stack>
        <CommitteeCard
          committee={item.committee}
          onClick={() => window.open(`/author/committee/${item.committee.id}`)}
        />
        <CommitteeOwnerProofList
          committeeId={item.committee.id}
          userId={userId}
        />
      </Stack>
    );
  }

  if (isEvalCriteriaDictionaryItem(item)) {
    return (
      <Stack>
        <DictionaryCard
          dictionary={item.dictionary}
          onClick={() => window.open(`/author/dictionary/${item.dictionary.id}`)}
        />
        <DictionaryOwnerProofList
          dictionaryId={item.dictionary.id}
          userId={userId}
        />
      </Stack>
    );
  }

  if (isEvalCriteriaAwardItem(item)) {
    return (
      <Stack>
        <AwardCard
          award={item.award}
          onClick={() => window.open(`/author/award/${item.award.id}`)}
        />
        <AwardOwnerProofList
          awardId={item.award.id}
          userId={userId}
        />
      </Stack>
    );
  }

  if (isEvalCriteriaPresentationItem(item)) {
    return (
      <Stack>
        <PresentationCard
          presentation={item.presentation}
          onClick={() => window.open(`/author/presentation/${item.presentation.id}`)}
        />
        <PresentationOwnerProofList
          presentationId={item.presentation.id}
          userId={userId}
        />
      </Stack>
    );
  }

  if (isEvalCriteriaEditorialTeamItem(item)) {
    return (
      <Stack>
        <EditorialTeamCard
          editorialTeam={item.editorialTeam}
          onClick={() => window.open(`/author/editorial-team/${item.editorialTeam.id}`)}
        />
        <EditorialTeamOwnerProofList
          editorialTeamId={item.editorialTeam.id}
          userId={userId}
        />
      </Stack>
    );
  }

  if (isEvalCriteriaDoctoralCommitteeItem(item)) {
    return (
      <Stack>
        <DoctoralCommitteeCard
          doctoralCommittee={item.doctoralCommittee}
          onClick={() => window.open(`/author/doctoral-committee/${item.doctoralCommittee.id}`)}
        />
        <DoctoralCommitteeOwnerProofList
          doctoralCommitteeId={item.doctoralCommittee.id}
          userId={userId}
        />
      </Stack>
    );
  }

  assertUnreachable(item);
}
